import type { ActivePlan, NextExposureOverlay, PlanItem, WorkoutHistoryItem } from '../api/types'

const dayKeyByNumber = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const calendarWeekDays = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']

export function toLocalIso(value: Date): string {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return year + '-' + month + '-' + day
}

export function moveMonth(value: Date, delta: number): Date {
  return new Date(value.getFullYear(), value.getMonth() + delta, 1, 12)
}

export type CalendarPlanEntry = {
  item: PlanItem
  overlay: NextExposureOverlay | null
}

export type CalendarCell = {
  date: Date
  iso: string
  workout: WorkoutHistoryItem | null
  planned: boolean
  planName: string | null
  planItems: PlanItem[]
  planEntries: CalendarPlanEntry[]
  deload: boolean
}

export function buildCalendar(
  month: Date,
  workouts: WorkoutHistoryItem[],
  plan: ActivePlan | undefined,
  todayIso = toLocalIso(new Date()),
): Array<CalendarCell | null> {
  const first = new Date(month.getFullYear(), month.getMonth(), 1, 12)
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const leading = (first.getDay() + 6) % 7
  const cells: Array<CalendarCell | null> = Array.from({ length: leading }, () => null)
  const byDate = new Map(workouts.map((workout) => [workout.localDate, workout]))
  const today = new Date(todayIso + 'T12:00:00')
  const overlayDateByPlanItem = new Map((plan?.items ?? [])
    .filter((item) => item.pendingNextExposureOverlay !== null)
    .map((item) => {
      const targetDay = dayKeyByNumber.indexOf(item.dayOfWeek)
      const daysUntilTarget = (targetDay - today.getDay() + 7) % 7
      const delta = daysUntilTarget === 0 ? 7 : daysUntilTarget
      const occurrence = new Date(today.getFullYear(), today.getMonth(), today.getDate() + delta, 12)
      return [item.id, toLocalIso(occurrence)]
    }))

  for (let day = 1; day <= days; day += 1) {
    const date = new Date(month.getFullYear(), month.getMonth(), day, 12)
    const iso = toLocalIso(date)
    const dayKey = dayKeyByNumber[date.getDay()]
    const planItems = (plan?.items ?? [])
      .filter((item) => item.dayOfWeek === dayKey)
      .sort((left, right) => left.sortOrder - right.sortOrder)
    const planEntries = planItems.map((item) => ({
      item,
      overlay: overlayDateByPlanItem.get(item.id) === iso
        ? item.pendingNextExposureOverlay
        : null,
    }))
    cells.push({
      date,
      iso,
      workout: byDate.get(iso) ?? null,
      planned: planItems.length > 0,
      planName: planItems[0]?.dayName ?? null,
      planItems,
      planEntries,
      deload: planEntries.some(({ item, overlay }) =>
        item.deloadActive || overlay?.purpose === 'Deload'),
    })
  }

  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

export function calendarState(cell: CalendarCell): { code: string; label: string } {
  if (cell.workout?.status === 'Completed') return { code: 'completed', label: 'Dokončeno' }
  if (cell.workout?.status === 'InProgress') return { code: 'progress', label: 'Rozpracováno' }
  if (cell.workout?.status === 'Abandoned') return { code: 'abandoned', label: 'Opuštěno' }
  if (cell.deload && (cell.workout?.status === 'Planned' || cell.planned)) {
    return { code: 'deload', label: 'Deload' }
  }
  if (cell.workout?.status === 'Planned' || cell.planned) return { code: 'planned', label: 'Trénink' }
  return { code: 'rest', label: 'Volno' }
}

export function canPreviewCalendarCell(cell: CalendarCell, todayIso: string): boolean {
  return cell.iso > todayIso
    && cell.planItems.length > 0
    && (!cell.workout || cell.workout.status === 'Planned')
}